import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { BaseFieldComponent } from '../base-field.component';
import { ButtonRadioGroupComponent, ButtonRadioOption } from '../../../../../components/button-radio-group/button-radio-group.component';

/**
 * Value structure for TECHNICAL_ITEM fields.
 * Matches the legacy showreport technical item checks.
 */
export interface TechnicalItemValue {
    check: boolean | null;
    message: string;
}

@Component({
    selector: 'app-technical-item-field',
    imports: [IonicModule, FormsModule, ButtonRadioGroupComponent],
    template: `
        <ion-item lines="none">
            <ion-label>{{ field.label }}</ion-label>
        </ion-item>
        <div class="technical-item-content">
            <app-button-radio-group
                [options]="checkOptions"
                [value]="itemValue.check"
                [disabled]="readonly"
                (valueChange)="onCheckChange($event)"
            ></app-button-radio-group>
            <ion-textarea
                [placeholder]="field.placeholder || 'Toelichting...'"
                [value]="messageValue"
                [disabled]="readonly"
                [rows]="3"
                (ionInput)="onMessageInput($event)"
                class="technical-item-textarea"
            ></ion-textarea>
        </div>
        @if (field.helpText) {
            <ion-note class="field-help">{{ field.helpText }}</ion-note>
        }
    `,
    styles: [`
        .technical-item-content {
            padding: 0 16px 8px;
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
        .technical-item-textarea {
            --background: var(--ion-color-light);
            --padding-start: 12px;
            --padding-end: 12px;
            --padding-top: 8px;
            --padding-bottom: 8px;
            border-radius: 8px;
        }
        .field-help {
            padding: 4px 16px;
            font-size: 12px;
        }
    `]
})
export class TechnicalItemFieldComponent extends BaseFieldComponent {
    readonly checkOptions: ButtonRadioOption[] = [
        {
            value: true,
            label: 'Zoals ontworpen',
            icon: 'checkmark',
            selectedIconColor: 'success'
        },
        {
            value: false,
            label: 'Afwijkend gedrag',
            icon: 'close',
            selectedIconColor: 'danger'
        }
    ];

    itemValue: TechnicalItemValue = {
        check: null,
        message: ''
    };

    // Separate property to track the textarea value
    messageValue: string = '';

    initialize(value: any): void {
        if (value && typeof value === 'object') {
            this.itemValue = {
                check: value.check ?? null,
                message: value.message ?? ''
            };
        } else if (typeof value === 'string') {
            // Try parsing JSON string from API
            try {
                const parsed = JSON.parse(value);
                this.itemValue = {
                    check: parsed.check ?? null,
                    message: parsed.message ?? ''
                };
            } catch {
                this.itemValue = { check: null, message: '' };
            }
        } else {
            this.itemValue = { check: false, message: '' };
        }
        // Sync messageValue for ngModel
        this.messageValue = this.itemValue.message;
    }

    serialize(): TechnicalItemValue {
        return this.itemValue;
    }

    onCheckChange(checked: boolean): void {
        this.itemValue = { ...this.itemValue, check: checked };
        this.emitChange(this.itemValue);
    }

    onMessageInput(event: Event): void {
        // For ion-textarea, ionInput's detail is the native InputEvent, not { value }
        // We need to get the value from the target element
        const target = event.target as HTMLTextAreaElement & { value: string };
        const value = target.value ?? '';
        this.messageValue = value;
        this.itemValue = { ...this.itemValue, message: value };
        this.emitChange(this.itemValue);
    }
}
